import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { analyze } from "../lib/api";
import { toast } from "sonner";
import { Wand2, Copy, Check, Plus, Minus } from "lucide-react";
import ReturnToProverb from "../components/ReturnToProverb";

const AUDIENCES = [
  "General",
  "Romantic Partner",
  "Spouse",
  "Family",
  "Parent",
  "Child / Teen",
  "Friend",
  "Coworker",
  "Boss",
  "Ex",
  "Stranger",
];

const EXAMPLES = [
  {
    label: "Romantic · Angry",
    audience: "Romantic Partner",
    text:
      "I honestly don't understand why you keep doing this. You say you care, but your actions show the opposite. I waited around all night while you couldn't even send a quick update. It feels like my time doesn't matter to you at all, and I'm tired of always being the one chasing you for basic respect.",
  },
  {
    label: "Romantic · Hurt",
    audience: "Romantic Partner",
    text:
      "I know you probably didn't mean to hurt me, but what you said really stuck with me. I've been replaying it in my head all day and it made me feel like I don't matter as much as I thought I did.",
  },
  {
    label: "Dating · Frustrated",
    audience: "Romantic Partner",
    text:
      "It's confusing when you disappear for days and then act like everything is normal. I don't know where I stand with you, and honestly it's exhausting.",
  },
  {
    label: "Family · Tension",
    audience: "Family",
    text:
      "I'm tired of feeling judged every time we talk. It seems like nothing I do is ever good enough, and it makes me not want to share anything with you.",
  },
  {
    label: "Friend · Disappointed",
    audience: "Friend",
    text:
      "You said you'd be there and you weren't. I get that life is busy but it really hurt that you didn't even let me know.",
  },
  {
    label: "Coworker · Annoyed",
    audience: "Coworker",
    text:
      "Once again I'm picking up the slack on your part of the project. I shouldn't have to keep covering for missed deadlines.",
  },
  {
    label: "Boss · Pushback",
    audience: "Boss",
    text:
      "This timeline is unrealistic and I've raised concerns multiple times. If we keep going like this something is going to slip and it won't be on me.",
  },
  {
    label: "Spouse · Cold",
    audience: "Spouse",
    text:
      "Fine. Whatever. Do what you want.",
  },
];

const flagStyle = (f) => {
  const lower = f.toLowerCase();
  if (lower.includes("harsh") || lower.includes("accus") || lower.includes("demand")) return "bg-[#F5E6D3] text-[#A86B4A]";
  if (lower.includes("passive") || lower.includes("guilt")) return "bg-[#F5EBD9] text-[#8C7A4F]";
  if (lower.includes("cold") || lower.includes("needy") || lower.includes("unclear")) return "bg-[#EAF1F2] text-[#5C7E86]";
  return "bg-[#E1ECDD] text-[#4F6F52]";
};

const REWRITE_STYLES = [
  { key: "calm", title: "Calm", subtitle: "Gentle and de-escalating", chip: "bg-[#E1ECDD] text-[#4F6F52]" },
  { key: "direct", title: "Direct", subtitle: "Clear and confident", chip: "bg-[#EAF1F2] text-[#5C7E86]" },
  { key: "warm", title: "Warm", subtitle: "Loving and connection-focused", chip: "bg-[#F5EBD9] text-[#8C7A4F]" },
  { key: "biblical", title: "Biblical", subtitle: "Rooted in grace and humility", chip: "bg-[#F5E6D3] text-[#A86B4A]" },
];

export default function AnalyzePage() {
  const [params] = useSearchParams();
  const [msg, setMsg] = useState("");
  const [audience, setAudience] = useState("General");
  const [prior, setPrior] = useState("");
  const [showContext, setShowContext] = useState(false);
  const [context, setContext] = useState("");
  const [showExamples, setShowExamples] = useState(true);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");

  // On mount, honor URL prefill from Guidance ("Say It Better" continuation
  // action) and always scroll to the top so the user starts fresh. Prefill
  // comes via query params (`?message=…&audience=…&prior=…`) which the
  // caller has already URL-encoded. Kept small so a hostile deep-link
  // cannot inject unbounded content.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
    const pm = params.get("message");
    const pa = params.get("audience");
    const pp = params.get("prior");
    if (pm) setMsg(pm.slice(0, 1500));
    if (pa) setAudience(pa.slice(0, 60));
    if (pp) {
      setPrior(pp.slice(0, 800));
    }
    if (pm) setShowExamples(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (e) => {
    e.preventDefault();
    if (!msg.trim() || busy) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await analyze.run({
        message: msg,
        audience,
        prior_message: prior || undefined,
        extra_context: context || undefined,
      });
      setResult(r);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Let’s try that again");
    } finally {
      setBusy(false);
    }
  };

  const copy = (label, text) => {
    navigator.clipboard?.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(""), 1500);
  };

  const loadExample = (ex) => {
    setMsg(ex.text);
    setAudience(ex.audience);
    setShowExamples(false);
    setResult(null);
  };

  const tone = result?.tone_score ?? 5;
  const toneColor =
    tone >= 8 ? "text-[#4F6F52] bg-[#E1ECDD]" : tone >= 5 ? "text-[#8C7A4F] bg-[#F5EBD9]" : "text-[#A86B4A] bg-[#F5E6D3]";
  const toneLabel = tone >= 8 ? "Lands kindly" : tone >= 5 ? "Could land harsh" : "Likely to wound";

  return (
    <div className="space-y-7">
      {/* "Return to I Need a Proverb" — renders only when a validated
          Proverb return URL is persisted (see ReturnToProverb component
          for the host allowlist). Kept subtle so it never competes with
          the primary Say It Better CTA. */}
      {typeof window !== "undefined" && localStorage.getItem("lg_proverb_return") && (
        <div className="fade-in-up">
          <ReturnToProverb testid="analyze-return-to-proverb" />
        </div>
      )}
      <div className="fade-in-up">
        <h1 className="font-display text-3xl text-[#2E2E2E]">Say it better</h1>
        <p className="text-sm text-[#6B6B6B] mt-1.5 leading-relaxed">
          Know how your message will land before you send it. Honest, kind, and constructive.
        </p>
      </div>

      <form onSubmit={run} className="rounded-3xl border border-[#DCE5DB] bg-white p-6 space-y-5 shadow-[0_4px_12px_rgba(0,0,0,0.04)] fade-in-up stagger-1">
        {/* Audience */}
        <div>
          <label className="block text-sm text-[#6B6B6B] mb-2">I’m messaging my…</label>
          <div className="flex flex-wrap gap-2" data-testid="audience-picker">
            {AUDIENCES.map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => setAudience(a)}
                data-testid={`audience-${a.replace(/\s+/g, "-").toLowerCase()}`}
                className={`text-sm rounded-full px-3.5 py-1.5 border transition ${
                  audience === a
                    ? "bg-[#4F6F52] text-white border-[#4F6F52]"
                    : "bg-white text-[#2E2E2E] border-[#DCE5DB] hover:border-[#4F6F52]/40"
                }`}
              >
                {a}
              </button>
            ))}
          </div>
        </div>

        {/* Prior message */}
        <div>
          <label className="block text-sm text-[#6B6B6B] mb-1.5">What did they say to you? <span className="text-[#a8a8a8]">(optional)</span></label>
          <textarea
            data-testid="analyze-prior"
            rows={2}
            value={prior}
            onChange={(e) => setPrior(e.target.value)}
            placeholder="Their last message, if relevant..."
            className="w-full bg-[#F9FAF7] border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition leading-relaxed"
          />
        </div>

        {/* Main message */}
        <div>
          <label className="block text-sm text-[#6B6B6B] mb-1.5">What do you want to say?</label>
          <textarea
            data-testid="analyze-input"
            rows={5}
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            placeholder="Paste the message you’re about to send..."
            className="w-full bg-[#F9FAF7] border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition leading-relaxed"
          />
        </div>

        {/* Extra context toggle */}
        <div>
          <button
            type="button"
            onClick={() => setShowContext((v) => !v)}
            className="text-sm text-[#4F6F52] inline-flex items-center gap-1 hover:underline"
            data-testid="toggle-context"
          >
            {showContext ? <Minus size={14} /> : <Plus size={14} />}
            {showContext ? "Hide context" : "Add more context (optional)"}
          </button>
          {showContext && (
            <textarea
              data-testid="analyze-context"
              rows={2}
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="History, what's been going on, what you really want them to understand..."
              className="mt-2 w-full bg-[#F9FAF7] border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition leading-relaxed"
            />
          )}
        </div>

        <div className="flex justify-end">
          <button
            data-testid="analyze-submit"
            type="submit"
            disabled={busy || !msg.trim()}
            className="bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-5 py-3 text-sm font-medium inline-flex items-center gap-2 transition disabled:opacity-60 shadow-[0_4px_14px_rgba(79,111,82,0.18)]"
          >
            <Wand2 size={14} /> {busy ? "Reading carefully..." : "Rewrite my message"}
          </button>
        </div>
      </form>

      {/* Examples */}
      {showExamples && !result && !busy && (
        <section className="fade-in-up stagger-2" data-testid="examples-section">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="font-display text-lg text-[#2E2E2E]">Try an example message</h2>
              <p className="text-xs text-[#6B6B6B]">Tap any to load it into the message box.</p>
            </div>
            <button
              type="button"
              onClick={() => setShowExamples(false)}
              className="text-xs text-[#6B6B6B] hover:text-[#4F6F52] underline-offset-4 hover:underline"
            >
              Hide
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {EXAMPLES.map((ex, i) => (
              <button
                key={i}
                type="button"
                onClick={() => loadExample(ex)}
                data-testid={`example-${i}`}
                className="text-left bg-white border border-[#DCE5DB] hover:border-[#4F6F52]/40 hover:shadow-[0_4px_14px_rgba(79,111,82,0.06)] rounded-2xl p-4 transition"
              >
                <div className="text-xs uppercase tracking-[0.14em] text-[#7FA7B0] font-semibold mb-1.5">{ex.label}</div>
                <div className="text-sm text-[#2E2E2E] line-clamp-3 leading-relaxed">“{ex.text}”</div>
              </button>
            ))}
          </div>
        </section>
      )}

      {busy && <div className="h-40 rounded-3xl shimmer" />}

      {result && (
        <div data-testid="analyze-result" className="space-y-4 fade-in-up">
          {/* How it lands */}
          <div className="rounded-3xl border border-[#DCE5DB] bg-[#EEF3EC] p-6 shadow-[0_4px_12px_rgba(0,0,0,0.04)]">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <span className={`rounded-full px-4 py-1.5 text-sm font-medium ${toneColor}`}>
                {toneLabel} · {tone}/10
              </span>
              <span className="text-sm text-[#6B6B6B]">
                Risk of escalation: <span className="font-medium text-[#2E2E2E] capitalize">{result.risk_of_escalation}</span>
              </span>
            </div>
            {result.how_it_lands && (
              <div className="rounded-2xl bg-white border border-[#DCE5DB] p-4 mb-4">
                <div className="text-xs uppercase tracking-[0.14em] text-[#7FA7B0] font-semibold mb-1.5">How this will land</div>
                <p className="text-[15px] text-[#2E2E2E] leading-[1.75]">{result.how_it_lands}</p>
              </div>
            )}
            {result.flags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {result.flags.map((f, i) => (
                  <span key={i} className={`text-xs rounded-full px-3 py-1 font-medium ${flagStyle(f)}`}>
                    {f}
                  </span>
                ))}
              </div>
            )}
            {result.issues?.length > 0 && (
              <ul className="text-sm text-[#2E2E2E] space-y-2 leading-relaxed">
                {result.issues.map((it, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#4F6F52] shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Rewrites */}
          <div>
            <h3 className="font-display text-lg text-[#2E2E2E] mb-3 px-1">A better way to say it</h3>
            <div className="space-y-3">
              {REWRITE_STYLES.map((rw) => {
                const txt = result.rewrites?.[rw.key];
                if (!txt) return null;
                return (
                  <div key={rw.key} className="rounded-3xl border border-[#DCE5DB] bg-white p-6 shadow-[0_4px_12px_rgba(0,0,0,0.04)]">
                    <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                      <div className="flex items-center gap-2.5">
                        <span className={`text-xs rounded-full px-3 py-1 font-medium ${rw.chip}`}>{rw.title}</span>
                        <span className="text-xs text-[#6B6B6B]">{rw.subtitle}</span>
                      </div>
                      <button
                        data-testid={`copy-${rw.key}`}
                        onClick={() => copy(rw.key, txt)}
                        className="text-xs text-[#4F6F52] inline-flex items-center gap-1 hover:underline"
                      >
                        {copied === rw.key ? <Check size={12} /> : <Copy size={12} />}
                        {copied === rw.key ? "Copied" : "Copy"}
                      </button>
                    </div>
                    <p className="text-[#2E2E2E] text-[15px] leading-[1.85]">{txt}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
